import { ICartItem } from '@dtos/Order'
import { FormattedOrderType, useOrderQuery } from '@queries/order'
import { useToastController } from '@tamagui/toast'
import { GhostLoading } from '@components/order/GhostLoading'
import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  ReactNode,
} from 'react'
import { useTranslation } from 'react-i18next'
import {
  QueryObserverResult,
  RefetchOptions,
  RefetchQueryFilters,
} from 'react-query'

type OrderDetailsContextType = FormattedOrderType & {
  refetch: <TPageData>(
    options?: (RefetchOptions & RefetchQueryFilters<TPageData>) | undefined,
  ) => Promise<QueryObserverResult<FormattedOrderType, unknown>>
  // HET-3540: detalhe aberto focado num item da venda decomposta (?item=<cartItemId>)
  selectedItem?: ICartItem
}

const OrderDetailsContext = createContext<OrderDetailsContextType>(
  {} as OrderDetailsContextType,
)

interface IOrderDetailsProviderProps {
  children: ReactNode
  orderId: string
  isPublic?: boolean
  itemId?: string
}

export const OrderDetailsProvider: React.FC<IOrderDetailsProviderProps> = ({
  children,
  orderId,
  isPublic,
  itemId,
}) => {
  const { data: order, isLoading, refetch } = useOrderQuery(orderId, isPublic)
  const toast = useToastController()
  const { t } = useTranslation('toast')

  useEffect(() => {
    if (!isLoading && !order) {
      toast.show(t('apolloErrors.orderDetails.title'), {
        message: t('apolloErrors.orderDetails.message'),
        type: 'error',
      })
    }
  }, [isLoading, order, toast, t])

  const contextValue = useMemo(() => {
    if (!order) return undefined

    const selectedItem = itemId
      ? order.allItems.find((item) => String(item.id) === String(itemId))
      : undefined

    // Detalhe focado num item: só os pagamentos que cobriram AQUELE item (bump em
    // assinatura no cartão tem payment próprio — homologação 28/08: a página do bump
    // listava as cobranças do pedido inteiro). Payment sem cart_item_ids (venda sem
    // decomposição) continua aparecendo.
    const payments = selectedItem
      ? order.payments.filter(
          (payment) =>
            !payment.cartItemIds?.length ||
            payment.cartItemIds.some(
              (cartItemId) => String(cartItemId) === String(selectedItem.id),
            ),
        )
      : order.payments

    return { ...order, payments, refetch, selectedItem }
  }, [order, refetch, itemId])

  if (isLoading || !contextValue) return <GhostLoading />

  return (
    <OrderDetailsContext.Provider value={contextValue}>
      {children}
    </OrderDetailsContext.Provider>
  )
}

export function useOrderDetails<T extends OrderDetailsContextType>() {
  const context = useContext(OrderDetailsContext) as T
  if (!context) {
    throw new Error(
      'useOrderDetails must be used within an OrderDetailsProvider',
    )
  }
  return context
}
